import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { tenantsApi, Tenant, UpdateTenantRequest } from '../services/tenantsApi'

interface TenantsState {
  tenants: Tenant[]
  loading: boolean
  error: string | null
}

interface TenantsActions {
  fetchTenants: () => Promise<void>
  createTenant: (tenantData: { name: string }) => Promise<void>
  updateTenant: (tenantId: string, tenantData: UpdateTenantRequest) => Promise<void>
  deleteTenant: (tenantId: string) => Promise<void>
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  clearError: () => void
}

type TenantsStore = TenantsState & TenantsActions

export const useTenantsStore = create<TenantsStore>()(
  persist(
    (set, get) => ({
      // Initial state
      tenants: [],
      loading: false,
      error: null,

      // Actions
      fetchTenants: async () => {
        set({ loading: true, error: null })
        
        try {
          const response = await tenantsApi.getTenants()
          console.log(response)
          if (response.success && response.data) {
            set({
              tenants: response.data,
              loading: false,
              error: null,
            })
          } else {
            set({
              tenants: [],
              loading: false,
              error: 'Failed to fetch tenants',
            })
          }
        } catch (error: any) {
          set({
            tenants: [],
            loading: false,
            error: error.message || 'Failed to fetch tenants',
          })
        }
      },

      createTenant: async (tenantData: { name: string }) => {
        set({ loading: true, error: null })
        
        try {
          const response = await tenantsApi.createTenant(tenantData)
          
          if (response.success && response.data) {
            const { tenants } = get()
            // response.data is the tenant object itself
            const newTenant = (response.data as any).data || response.data
            set({
              tenants: [...tenants, newTenant],
              loading: false,
              error: null,
            })
          } else {
            set({
              loading: false,
              error: 'Failed to create tenant',
            })
          }
        } catch (error: any) {
          set({
            loading: false,
            error: error.message || 'Failed to create tenant',
          })
        }
      },

      updateTenant: async (tenantId: string, tenantData: UpdateTenantRequest) => {
        set({ loading: true, error: null })
        
        try {
          const response = await tenantsApi.updateTenant(tenantId, tenantData)
          console.log('Update tenant response:', response)
          
          if (response.success && response.data) {
            const { tenants } = get()
            set({
              tenants: tenants.map(tenant => 
                tenant._id === tenantId ? response.data! : tenant
              ),
              loading: false,
              error: null,
            })
          } else {
            console.error('Update tenant failed:', response)
            set({
              loading: false,
              error: 'Failed to update tenant',
            })
          }
        } catch (error: any) {
          console.error('Update tenant error:', error)
          set({
            loading: false,
            error: error.message || 'Failed to update tenant',
          })
        }
      },

      deleteTenant: async (tenantId: string) => {
        set({ loading: true, error: null })
        
        try {
          const response = await tenantsApi.deleteTenant(tenantId)
          
          if (response.success) {
            const { tenants } = get()
            set({
              tenants: tenants.filter(tenant => tenant._id !== tenantId),
              loading: false,
              error: null,
            })
          } else {
            set({
              loading: false,
              error: 'Failed to delete tenant',
            })
          }
        } catch (error: any) {
          set({
            loading: false,
            error: error.message || 'Failed to delete tenant',
          })
        }
      },

      setLoading: (loading: boolean) => {
        set({ loading })
      },

      setError: (error: string | null) => {
        set({ error })
      },

      clearError: () => {
        set({ error: null })
      },
    }),
    {
      name: 'tenants-storage',
      partialize: (state) => ({
        tenants: state.tenants,
      }),
    }
  )
)
